import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Building2, Users, Sparkles, ArrowRight, Star, BadgeCheck, Crown, Phone, ChevronRight, ChevronDown } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { NewsletterForm } from '../components/Newsletter';
import { API } from '../utils/api';
import NotFoundPage from './NotFoundPage';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";


// Helper function to get the correct image URL
function getImageUrl(url) {
  if (!url) return null;
  if (url.includes('.emergent.host/api/') || url.includes('.emergentagent.com/api/')) {
    try { url = new URL(url).pathname; } catch(e) {}
  }
  if (url.startsWith('/api/')) {
    var base = process.env.REACT_APP_BACKEND_URL;
    if (base && !base.includes('localhost') && typeof window !== 'undefined' && !base.includes(window.location.hostname)) {
      base = window.location.origin;
    }
    return (base || '') + url;
  }
  return url;
}

// Chilean cities with SEO data
var CHILEAN_CITIES = [
  { slug: 'santiago', name: 'Santiago', region: 'Metropolitana', population: '5.6M', description: 'Capital de Chile, centro de servicios de rehabilitación más grande del país' },
  { slug: 'valparaiso', name: 'Valparaíso', region: 'Valparaíso', population: '300K', description: 'Ciudad portuaria con amplia oferta de centros de tratamiento' },
  { slug: 'concepcion', name: 'Concepción', region: 'Biobío', population: '230K', description: 'Principal ciudad del sur de Chile con servicios especializados' },
  { slug: 'antofagasta', name: 'Antofagasta', region: 'Antofagasta', population: '400K', description: 'Centro de rehabilitación del norte de Chile' },
  { slug: 'vina-del-mar', name: 'Viña del Mar', region: 'Valparaíso', population: '330K', description: 'Ciudad jardín con centros de tratamiento de alta calidad' },
  { slug: 'temuco', name: 'Temuco', region: 'La Araucanía', population: '280K', description: 'Capital de La Araucanía con servicios de adicciones' },
  { slug: 'puerto-montt', name: 'Puerto Montt', region: 'Los Lagos', population: '250K', description: 'Puerta de entrada a la Patagonia chilena' },
  { slug: 'la-serena', name: 'La Serena', region: 'Coquimbo', population: '220K', description: 'Ciudad costera del norte chico' },
  { slug: 'arica', name: 'Arica', region: 'Arica y Parinacota', population: '240K', description: 'Ciudad más septentrional de Chile' },
  { slug: 'rancagua', name: 'Rancagua', region: "O'Higgins", population: '240K', description: 'Capital regional cercana a Santiago' },
  { slug: 'talca', name: 'Talca', region: 'Maule', population: '220K', description: 'Centro de servicios de la zona central sur' },
  { slug: 'chillan', name: 'Chillán', region: 'Ñuble', population: '180K', description: 'Capital de la nueva región de Ñuble' },
  { slug: 'iquique', name: 'Iquique', region: 'Tarapacá', population: '190K', description: 'Ciudad portuaria del norte grande' },
  { slug: 'coquimbo', name: 'Coquimbo', region: 'Coquimbo', population: '160K', description: 'Puerto del norte chico de Chile' },
  { slug: 'punta-arenas', name: 'Punta Arenas', region: 'Magallanes', population: '130K', description: 'Ciudad más austral de Chile continental' }
];

export function CitiesDirectoryPage() {
  var [centers, setCenters] = useState([]);
  var [professionals, setProfessionals] = useState([]);
  var [workshops, setWorkshops] = useState([]);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      var [centersRes, professionalsRes, workshopsRes] = await Promise.all([
        axios.get(API + '/centers/public?limit=100'),
        axios.get(API + '/professionals/public?limit=100'),
        axios.get(API + '/workshops/public?limit=100').catch(function() { return { data: { workshops: [] } }; })
      ]);
      setCenters(centersRes.data.centers || []);
      setProfessionals(professionalsRes.data.professionals || []);
      setWorkshops(workshopsRes.data.workshops || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  // Count entities by city
  function getCountByCity(citySlug) {
    var cityName = CHILEAN_CITIES.find(function(c) { return c.slug === citySlug; })?.name || '';
    var centerCount = centers.filter(function(c) { 
      return c.city && c.city.toLowerCase().includes(cityName.toLowerCase()); 
    }).length;
    var professionalCount = professionals.filter(function(p) { 
      return p.city && p.city.toLowerCase().includes(cityName.toLowerCase()); 
    }).length;
    var workshopCount = workshops.filter(function(w) { 
      return w.city && w.city.toLowerCase().includes(cityName.toLowerCase()); 
    }).length;
    return { centers: centerCount, professionals: professionalCount, workshops: workshopCount, total: centerCount + professionalCount + workshopCount };
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      
        <title>Centros de Rehabilitación de Drogas en Chile | Directorio por Ciudad 2026 | SinAdicciones.org</title>
        <meta name="description" content="Directorio completo de centros de rehabilitación de drogas en Chile. Encuentra clínicas de tratamiento de adicciones, alcoholismo y drogas en Santiago, Valparaíso, Concepción y todas las ciudades. Precios, opiniones y contacto directo." />
        <meta name="keywords" content="centros de rehabilitación de drogas en chile, centros rehabilitación drogas Chile, clinicas de rehabilitacion de drogas, tratamiento adicciones Chile, centros desintoxicación, rehabilitación alcohol drogas, clinica rehabilitacion alcoholismo, centros tratamiento adicciones Santiago, rehabilitacion drogas Valparaiso, centros de ayuda para drogadictos" />
        <link rel="canonical" href="https://sinadicciones.org/ciudades" />
        
        <meta property="og:title" content="Centros de Rehabilitación de Drogas en Chile - Directorio por Ciudad" />
        <meta property="og:description" content="Encuentra centros de rehabilitación de drogas y alcohol en todas las ciudades de Chile. Directorio actualizado 2026 con precios, opiniones y contacto directo." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/ciudades" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Centros de Rehabilitación de Drogas en Chile - Por Ciudad" />
        <meta name="twitter:description" content="Directorio nacional con precios, opiniones y contacto directo en todas las ciudades de Chile." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Centros de Rehabilitación de Drogas en Chile - Directorio por Ciudad",
          "description": "Directorio completo de centros de rehabilitación de drogas y alcohol en todas las ciudades de Chile",
          "url": "https://sinadicciones.org/ciudades",
          "publisher": {
            "@type": "Organization",
            "name": "SinAdicciones.org",
            "url": "https://sinadicciones.org"
          },
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": CHILEAN_CITIES.length,
            "itemListElement": CHILEAN_CITIES.map(function(city, idx) {
              return {
                "@type": "ListItem",
                "position": idx + 1,
                "name": "Centros de rehabilitación de drogas en " + city.name,
                "url": "https://sinadicciones.org/ciudad/centro-rehabilitacion-" + city.slug
              };
            })
          }
        })}</script>
        
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "¿Cuál es el mejor centro de rehabilitación de drogas en Chile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "El mejor centro depende de las necesidades específicas de cada persona. En SinAdicciones.org listamos centros verificados en todo Chile con calificaciones de usuarios, precios y especialidades. Recomendamos comparar opciones según ubicación, tipo de tratamiento (residencial o ambulatorio), precio y opiniones de pacientes."
              }
            },
            {
              "@type": "Question",
              "name": "¿Cuánto cuesta un tratamiento de rehabilitación en Chile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Los precios varían según el tipo de tratamiento. Los programas ambulatorios cuestan entre $200.000 y $800.000 mensuales. Los tratamientos residenciales van desde $500.000 hasta $5.000.000 mensuales dependiendo de las instalaciones. También existen opciones gratuitas a través de SENDA y FONASA."
              }
            },
            {
              "@type": "Question",
              "name": "¿Existen centros de rehabilitación gratuitos en Chile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, SENDA (Servicio Nacional para la Prevención y Rehabilitación del Consumo de Drogas y Alcohol) ofrece tratamiento gratuito a través de su red de centros. También puedes acceder con FONASA a centros privados con convenio. Llama al 1412 para orientación gratuita."
              }
            },
            {
              "@type": "Question",
              "name": "¿Cuánto tiempo dura una rehabilitación de drogas?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "La duración varía según la gravedad de la adicción y el tipo de tratamiento. Los programas ambulatorios duran entre 3 y 6 meses. Los tratamientos residenciales pueden ir de 28 días hasta 12 meses. La mayoría de los expertos recomiendan un mínimo de 90 días para mejores resultados."
              }
            },
            {
              "@type": "Question",
              "name": "¿Qué tipos de adicciones tratan los centros en Chile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Los centros en Chile tratan diversas adicciones: alcoholismo, cocaína, pasta base, marihuana, medicamentos, ludopatía (juego), adicción a tecnología y otras dependencias. Muchos ofrecen programas especializados según el tipo de sustancia y perfil del paciente."
              }
            }
          ]
        })}</script>
      
      
      <Header />
      
      <main className="container mx-auto px-4 pt-24 pb-12 max-w-6xl">
        {/* Hero Section - Improved SEO */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
            <MapPin className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-400">Directorio actualizado 2026</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Centros de Rehabilitación de<br />
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">Drogas en Chile</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-8">
            El directorio más completo de clínicas y centros de tratamiento de adicciones en Chile. 
            Encuentra ayuda profesional para superar la drogadicción y el alcoholismo cerca de ti.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-sm text-slate-500">
            <span className="px-3 py-1 bg-slate-800 rounded-full">✓ Centros verificados</span>
            <span className="px-3 py-1 bg-slate-800 rounded-full">✓ Precios actualizados</span>
            <span className="px-3 py-1 bg-slate-800 rounded-full">✓ Opiniones reales</span>
            <span className="px-3 py-1 bg-slate-800 rounded-full">✓ Contacto directo</span>
          </div>
        </div>

        {/* Stats - Improved Design */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 hover:border-emerald-500/40 transition-all">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-emerald-400" />
              </div>
              <p className="text-3xl font-bold text-emerald-400">{centers.length}</p>
              <p className="text-sm text-slate-400">Centros de Rehabilitación</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 hover:border-blue-500/40 transition-all">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <p className="text-3xl font-bold text-blue-400">{professionals.length}</p>
              <p className="text-sm text-slate-400">Profesionales</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20 hover:border-purple-500/40 transition-all">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-purple-400" />
              </div>
              <p className="text-3xl font-bold text-purple-400">{workshops.length}</p>
              <p className="text-sm text-slate-400">Talleres y Programas</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20 hover:border-amber-500/40 transition-all">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-amber-400" />
              </div>
              <p className="text-3xl font-bold text-amber-400">16</p>
              <p className="text-sm text-slate-400">Regiones de Chile</p>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : (
          <>
            {/* Cities Grid - Improved Design */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                Centros de Rehabilitación de Drogas por Ciudad
              </h2>
              <p className="text-slate-400">Selecciona tu ciudad para ver los centros disponibles</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {CHILEAN_CITIES.map(function(city) {
                var counts = getCountByCity(city.slug);
                var hasData = counts.total > 0;
                return (
                  <Link key={city.slug} to={'/ciudad/centro-rehabilitacion-' + city.slug}>
                    <Card className={'h-full transition-all cursor-pointer group ' + (hasData ? 'bg-slate-900/80 border-slate-800 hover:border-emerald-500/50 hover:bg-slate-900' : 'bg-slate-900/50 border-slate-800/50')}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold text-slate-50 group-hover:text-emerald-400 transition-colors">
                              {city.name}
                            </h3>
                            <p className="text-sm text-slate-500">{city.region}</p>
                          </div>
                          <div className={'w-10 h-10 rounded-full flex items-center justify-center transition-all ' + (hasData ? 'bg-emerald-500/20 group-hover:bg-emerald-500/30' : 'bg-slate-800')}>
                            <MapPin className={'w-5 h-5 ' + (hasData ? 'text-emerald-400' : 'text-slate-600')} />
                          </div>
                        </div>
                        <p className="text-sm text-slate-400 mb-4 line-clamp-2">{city.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm">
                            {counts.centers > 0 && (
                              <span className="flex items-center gap-1 text-emerald-400">
                                <Building2 className="w-4 h-4" />{counts.centers}
                              </span>
                            )}
                            {counts.professionals > 0 && (
                              <span className="flex items-center gap-1 text-blue-400">
                                <Users className="w-4 h-4" />{counts.professionals}
                              </span>
                            )}
                            {counts.workshops > 0 && (
                              <span className="flex items-center gap-1 text-purple-400">
                                <Sparkles className="w-4 h-4" />{counts.workshops}
                              </span>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400 transition-colors" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>

            {/* SEO Content Section */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 mb-16">
              <h2 className="text-2xl font-bold mb-6 text-center">
                ¿Buscas un Centro de Rehabilitación de Drogas en Chile?
              </h2>
              <div className="grid md:grid-cols-2 gap-8 text-slate-400">
                <div>
                  <h3 className="text-lg font-semibold text-slate-200 mb-3">¿Qué ofrecemos?</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">✓</span>
                      <span>Directorio completo de <strong className="text-slate-200">centros de rehabilitación de drogas</strong> en todas las ciudades de Chile</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">✓</span>
                      <span>Información verificada sobre <strong className="text-slate-200">clínicas de tratamiento de adicciones</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">✓</span>
                      <span>Precios actualizados y opiniones de pacientes reales</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">✓</span>
                      <span>Contacto directo con centros de <strong className="text-slate-200">rehabilitación de alcoholismo</strong></span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-200 mb-3">Tipos de tratamiento disponibles</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>Tratamiento residencial (internación)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>Tratamiento ambulatorio</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>Desintoxicación supervisada</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>Programas de día y noche</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>Terapia online y seguimiento</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* FAQ Section - Visible for users and SEO */}
            <div className="mt-16">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">Preguntas Frecuentes sobre Rehabilitación en Chile</h2>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="item-1" className="border-slate-700">
                      <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400">
                        ¿Cómo elegir el mejor centro de rehabilitación en Chile?
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-400">
                        Para elegir el mejor centro de rehabilitación, considera: la ubicación geográfica, el tipo de tratamiento (ambulatorio o residencial), las especialidades del centro, las opiniones de otros pacientes, los costos y formas de pago, y si cuentan con profesionales certificados. En SinAdicciones.org puedes comparar centros verificados con toda esta información.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-2" className="border-slate-700">
                      <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400">
                        ¿Cuánto cuesta un tratamiento de rehabilitación de drogas en Chile?
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-400">
                        Los costos varían según el tipo de tratamiento. Un programa ambulatorio puede costar entre $200.000 y $500.000 mensuales. Un tratamiento residencial puede ir desde $800.000 hasta $3.000.000 o más por mes, dependiendo de las instalaciones y servicios. Algunos centros ofrecen becas o convenios con FONASA e Isapres.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-3" className="border-slate-700">
                      <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400">
                        ¿Qué tipos de adicciones tratan los centros de rehabilitación?
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-400">
                        Los centros de rehabilitación en Chile tratan diversas adicciones: alcohol, drogas (cocaína, pasta base, marihuana, anfetaminas), medicamentos, adicciones conductuales (juego, tecnología, compras). Muchos centros también ofrecen tratamiento para trastornos duales (adicción + salud mental).
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-4" className="border-slate-700">
                      <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400">
                        ¿Cuánto tiempo dura un tratamiento de rehabilitación?
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-400">
                        La duración depende de cada caso. Los programas cortos duran 30 días, los estándar entre 60-90 días, y los programas extensivos 6 meses o más. La mayoría de especialistas recomienda un mínimo de 90 días para tratamientos residenciales, seguido de un programa ambulatorio de seguimiento.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-5" className="border-slate-700">
                      <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400">
                        ¿Es posible hacer tratamiento de adicciones online en Chile?
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-400">
                        Sí, existen opciones de tratamiento online para personas que no pueden acceder a un centro presencial. Incluyen terapia individual por videollamada, grupos de apoyo virtuales, y programas ambulatorios híbridos. Es especialmente útil para el seguimiento post-tratamiento y para quienes viven en ciudades sin centros especializados.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            </div>

            {/* CTA Section */}
            <Card className="bg-gradient-to-r from-emerald-900/50 to-teal-900/50 border-0">
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-bold mb-4">¿No encuentras tu ciudad?</h2>
                <p className="text-slate-400 mb-6 max-w-xl mx-auto">
                  Contamos con centros y profesionales en todo Chile. Usa nuestro buscador principal para encontrar opciones cercanas a ti.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Link to="/centros-de-rehabilitacion-drogas-chile">
                    <Button className="bg-emerald-500 hover:bg-emerald-600">
                      <Building2 className="w-4 h-4 mr-2" />Ver todos los centros
                    </Button>
                  </Link>
                  <Link to="/profesionales">
                    <Button variant="outline" className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10">
                      <Users className="w-4 h-4 mr-2" />Ver profesionales
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Newsletter Section */}
            <div className="mt-12">
              <NewsletterForm />
            </div>
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
}

export function CityDetailPage() {
  var { cityPath } = useParams();
  var navigate = useNavigate();
  
  // Check if URL needs redirect (old format without prefix)
  var hasPrefix = cityPath.startsWith('centro-rehabilitacion-');
  var citySlug = hasPrefix ? cityPath.replace('centro-rehabilitacion-', '') : cityPath;

  // Validar que la ciudad existe en nuestro catálogo (calculado antes de hooks
  // condicionales para no violar rules-of-hooks). Si no → 404 con noindex.
  var cityFromCatalog = CHILEAN_CITIES.find(function(c) { return c.slug === citySlug; });

  var [centers, setCenters] = useState([]);
  var [professionals, setProfessionals] = useState([]);
  var [workshops, setWorkshops] = useState([]);
  var [loading, setLoading] = useState(true);

  var cityData = cityFromCatalog || { name: '' };

  // Redirect old URLs to new SEO-friendly URLs
  useEffect(function() {
    if (cityFromCatalog && !hasPrefix) {
      navigate('/ciudad/centro-rehabilitacion-' + cityPath, { replace: true });
    }
  }, [hasPrefix, cityPath, navigate, cityFromCatalog]);

  useEffect(function() {
    // Only load data if URL has the correct prefix AND ciudad es válida
    if (cityFromCatalog && hasPrefix) {
      loadData();
    }
  }, [citySlug, hasPrefix, cityFromCatalog]);

  async function loadData() {
    setLoading(true);
    try {
      var [centersRes, professionalsRes, workshopsRes] = await Promise.all([
        axios.get(API + '/centers/public?city=' + encodeURIComponent(cityData.name) + '&limit=50'),
        axios.get(API + '/professionals/public?city=' + encodeURIComponent(cityData.name) + '&limit=50'),
        axios.get(API + '/workshops/public?city=' + encodeURIComponent(cityData.name) + '&limit=50').catch(function() { return { data: { workshops: [] } }; })
      ]);
      setCenters(centersRes.data.centers || []);
      setProfessionals(professionalsRes.data.professionals || []);
      setWorkshops(workshopsRes.data.workshops || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  // 404 si la ciudad no existe en el catálogo (evita Soft 404 reportados por GSC).
  // Se evalúa DESPUÉS de todos los hooks para no violar rules-of-hooks.
  if (!cityFromCatalog) {
    return <NotFoundPage />;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      
        <title>{'Centros de Rehabilitación de Drogas en ' + cityData.name + ' (2026) | Gratis y Privados — SinAdicciones.org'}</title>
        <meta name="description" content={'Directorio actualizado 2026 de centros de rehabilitación de drogas y alcohol en ' + cityData.name + ', ' + cityData.region + '. Opciones gratuitas (SENDA/FONASA) y privadas con precios, modalidad residencial o ambulatoria. Cotización gratis y confidencial.'} />
        <meta name="keywords" content={'centros rehabilitación drogas ' + cityData.name + ', rehabilitación drogas gratis ' + cityData.name + ', tratamiento alcoholismo ' + cityData.name + ', clínicas adicciones ' + cityData.region + ', SENDA ' + cityData.name + ', FONASA rehabilitación ' + cityData.name} />
        <link rel="canonical" href={'https://sinadicciones.org/ciudad/centro-rehabilitacion-' + citySlug} />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content={'https://sinadicciones.org/ciudad/centro-rehabilitacion-' + citySlug} />
        <meta property="og:title" content={'Centros de Rehabilitación de Drogas en ' + cityData.name + ' (2026) | Gratis y Privados'} />
        <meta property="og:description" content={'Directorio 2026 de centros de rehabilitación en ' + cityData.name + '. Gratis (SENDA/FONASA) y privados. Cotiza gratis.'} />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={'Centros de Rehabilitación en ' + cityData.name} />
        <meta name="twitter:description" content={'Directorio de centros en ' + cityData.name + '.'} />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Centros de Rehabilitación de Drogas y Alcohol en " + cityData.name,
          "description": "Directorio de centros de rehabilitación de drogas y alcohol en " + cityData.name + ", " + cityData.region,
          "about": {
            "@type": "City",
            "name": cityData.name,
            "containedInPlace": {
              "@type": "AdministrativeArea",
              "name": cityData.region
            }
          }
        })}</script>
        
        {/* Hidden marker for react-snap to detect page is ready */}
        <meta name="city-faq-ready" content={cityData.name} />
        
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "¿Dónde encontrar centros de rehabilitación en " + cityData.name + "?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "En SinAdicciones.org encontrarás un directorio completo de centros de rehabilitación verificados en " + cityData.name + " para el tratamiento de adicciones a drogas, alcohol y otras dependencias. Cada centro incluye información de contacto, precios y opiniones de pacientes."
              }
            },
            {
              "@type": "Question",
              "name": "¿Cuánto cuesta un tratamiento de rehabilitación en " + cityData.name + "?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Los precios de rehabilitación en " + cityData.name + " varían según el tipo de tratamiento. Los programas ambulatorios pueden costar desde $200.000 mensuales, mientras que los tratamientos residenciales van desde $500.000 a $3.000.000 mensuales. También existen opciones gratuitas a través de SENDA y FONASA."
              }
            },
            {
              "@type": "Question",
              "name": "¿Hay centros de rehabilitación gratuitos en " + cityData.name + "?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, en " + cityData.name + " puedes acceder a tratamiento gratuito a través de la red SENDA (Servicio Nacional de Drogas y Alcohol) o mediante convenio FONASA. Consulta los centros que aceptan estos convenios en nuestro directorio."
              }
            },
            {
              "@type": "Question",
              "name": "¿Qué tipos de tratamientos de adicciones ofrecen en " + cityData.name + "?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Los centros en " + cityData.name + " ofrecen diversos tratamientos: residencial (internación), ambulatorio, hospital de día, desintoxicación, terapia individual y grupal, y programas especializados para alcohol, drogas, ludopatía y otras adicciones."
              }
            },
            {
              "@type": "Question",
              "name": "¿Cuánto dura un tratamiento de rehabilitación?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "La duración depende del tipo de adicción y modalidad. Los programas ambulatorios suelen durar 3-6 meses, mientras que los tratamientos residenciales pueden ir de 1 a 12 meses. La evaluación inicial determinará el plan más adecuado para cada caso."
              }
            }
          ]
        })}</script>
      
      
      <Header />
      
      <main className="container mx-auto px-4 pt-24 pb-12 max-w-6xl">
        {/* City detail marker - used by react-snap to wait for dynamic content */}
        <div className="city-detail-loaded" data-city={citySlug} style={{display: 'none'}} />
        
        {/* Breadcrumb */}
        <nav className="text-sm mb-6">
          <ol className="flex items-center gap-2">
            <li><Link to="/" className="text-slate-500 hover:text-emerald-400">Inicio</Link></li>
            <li className="text-slate-600">/</li>
            <li><Link to="/ciudades" className="text-slate-500 hover:text-emerald-400">Ciudades</Link></li>
            <li className="text-slate-600">/</li>
            <li className="text-emerald-400">{cityData.name}</li>
          </ol>
        </nav>

        {/* Hero */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <MapPin className="w-7 h-7 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">
                Rehabilitación en <span className="text-emerald-400">{cityData.name}</span>
              </h1>
              <p className="text-slate-500">{cityData.region}, Chile</p>
            </div>
          </div>
          <p className="text-lg text-slate-400 max-w-2xl">{cityData.description}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-12">
          <Card className="bg-slate-900 border-0">
            <CardContent className="p-4 text-center">
              <Building2 className="w-6 h-6 mx-auto mb-2 text-emerald-400" />
              <p className="text-2xl font-bold">{centers.length}</p>
              <p className="text-xs text-slate-500">Centros</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-0">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 text-blue-400" />
              <p className="text-2xl font-bold">{professionals.length}</p>
              <p className="text-xs text-slate-500">Profesionales</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-0">
            <CardContent className="p-4 text-center">
              <Sparkles className="w-6 h-6 mx-auto mb-2 text-purple-400" />
              <p className="text-2xl font-bold">{workshops.length}</p>
              <p className="text-xs text-slate-500">Talleres</p>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : (
          <>
            {/* Centers */}
            {centers.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Building2 className="w-6 h-6 text-emerald-400" />
                    Centros de Rehabilitación
                  </h2>
                  <Link to={'/centros?city=' + encodeURIComponent(cityData.name)}>
                    <Button variant="outline" size="sm" className="border-slate-700">
                      Ver todos <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {centers.slice(0, 6).map(function(center) {
                    var rawImage = center.logo || (center.gallery && center.gallery[0]) || (center.images && center.images[0]) || null;
                    var centerImage = getImageUrl(rawImage);
                    return (
                      <Link key={center.center_id} to={'/centros/' + center.center_id}>
                        <Card className="bg-slate-900 border-0 hover:bg-slate-800 transition-all h-full">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              {centerImage ? (
                                <img src={centerImage} alt={center.name} className="w-16 h-16 rounded-lg object-cover" onError={function(e) { e.target.style.display = 'none'; }} />
                              ) : (
                                <div className="w-16 h-16 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                                  <Building2 className="w-8 h-8 text-emerald-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-slate-50 truncate">{center.name}</h3>
                                  {center.is_premium && <Crown className="w-4 h-4 text-amber-400" />}
                                  {center.is_verified && <BadgeCheck className="w-4 h-4 text-emerald-400" />}
                                </div>
                                <p className="text-sm text-slate-500 mb-2">{center.address || center.city}</p>
                                {center.average_rating > 0 && (
                                  <div className="flex items-center gap-1">
                                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                                    <span className="text-sm text-slate-400">{center.average_rating.toFixed(1)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Professionals */}
            {professionals.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Users className="w-6 h-6 text-blue-400" />
                    Profesionales
                  </h2>
                  <Link to={'/profesionales?city=' + encodeURIComponent(cityData.name)}>
                    <Button variant="outline" size="sm" className="border-slate-700">
                      Ver todos <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {professionals.slice(0, 4).map(function(prof) {
                    return (
                      <Link key={prof.user_id} to={'/profesionales/' + prof.user_id}>
                        <Card className="bg-slate-900 border-0 hover:bg-slate-800 transition-all h-full">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              {prof.profile_image ? (
                                <img src={prof.profile_image} alt={prof.name} className="w-14 h-14 rounded-full object-cover" />
                              ) : (
                                <div className="w-14 h-14 rounded-full bg-blue-500/20 flex items-center justify-center">
                                  <Users className="w-7 h-7 text-blue-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-slate-50 truncate">{prof.name}</h3>
                                  {prof.is_verified && <BadgeCheck className="w-4 h-4 text-emerald-400" />}
                                </div>
                                <p className="text-sm text-blue-400">{prof.professional_type}</p>
                                <p className="text-sm text-slate-500">{prof.specialization}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Workshops */}
            {workshops.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-purple-400" />
                    Talleres Terapéuticos
                  </h2>
                  <Link to={'/talleres?city=' + encodeURIComponent(cityData.name)}>
                    <Button variant="outline" size="sm" className="border-slate-700">
                      Ver todos <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {workshops.slice(0, 4).map(function(workshop) {
                    return (
                      <Link key={workshop.workshop_id} to={'/talleres/' + workshop.workshop_id}>
                        <Card className="bg-slate-900 border-0 hover:bg-slate-800 transition-all h-full">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                                <Sparkles className="w-6 h-6 text-purple-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-slate-50 truncate mb-1">{workshop.name}</h3>
                                <p className="text-sm text-purple-400">{workshop.workshop_type}</p>
                                <p className="text-sm text-slate-500">{workshop.modality}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Empty state */}
            {centers.length === 0 && professionals.length === 0 && workshops.length === 0 && (
              <Card className="bg-slate-900 border-0">
                <CardContent className="p-12 text-center">
                  <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                  <h3 className="text-xl font-semibold mb-2">No hay resultados en {cityData.name}</h3>
                  <p className="text-slate-500 mb-6">
                    Aún no tenemos centros registrados en esta ciudad, pero puedes buscar en ciudades cercanas.
                  </p>
                  <Link to="/ciudades">
                    <Button className="bg-emerald-500 hover:bg-emerald-600">
                      Ver otras ciudades
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* FAQ Section - Visible for users and SEO */}
            <section className="mt-12">
              <h2 className="text-xl font-bold mb-4">Preguntas Frecuentes sobre Rehabilitación en {cityData.name}</h2>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 md:p-6">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="faq-1" className="border-slate-700">
                      <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                        ¿Dónde encontrar centros de rehabilitación en {cityData.name}?
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-400 text-sm">
                        En SinAdicciones.org encontrarás un directorio completo de centros de rehabilitación verificados en {cityData.name} para el tratamiento de adicciones a drogas, alcohol y otras dependencias. Cada centro incluye información de contacto, precios y opiniones de pacientes.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="faq-2" className="border-slate-700">
                      <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                        ¿Cuánto cuesta un tratamiento de rehabilitación en {cityData.name}?
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-400 text-sm">
                        Los precios de rehabilitación en {cityData.name} varían según el tipo de tratamiento. Los programas ambulatorios pueden costar desde $200.000 mensuales, mientras que los tratamientos residenciales van desde $500.000 a $3.000.000 mensuales. También existen opciones gratuitas a través de SENDA y FONASA.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="faq-3" className="border-slate-700">
                      <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                        ¿Hay centros de rehabilitación gratuitos en {cityData.name}?
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-400 text-sm">
                        Sí, en {cityData.name} puedes acceder a tratamiento gratuito a través de la red SENDA (Servicio Nacional de Drogas y Alcohol) o mediante convenio FONASA. Consulta los centros que aceptan estos convenios en nuestro directorio.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="faq-4" className="border-slate-700">
                      <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                        ¿Qué tipos de tratamientos de adicciones ofrecen en {cityData.name}?
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-400 text-sm">
                        Los centros en {cityData.name} ofrecen diversos tratamientos: residencial (internación), ambulatorio, hospital de día, desintoxicación, terapia individual y grupal, y programas especializados para alcohol, drogas, ludopatía y otras adicciones.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="faq-5" className="border-slate-700">
                      <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                        ¿Cuánto dura un tratamiento de rehabilitación?
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-400 text-sm">
                        La duración depende del tipo de adicción y modalidad. Los programas ambulatorios suelen durar 3-6 meses, mientras que los tratamientos residenciales pueden ir de 1 a 12 meses. La evaluación inicial determinará el plan más adecuado para cada caso.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            </section>

            {/* Related cities */}
            <section className="mt-12">
              <h2 className="text-xl font-bold mb-4">Otras ciudades cercanas</h2>
              <div className="flex flex-wrap gap-2">
                {CHILEAN_CITIES.filter(function(c) { return c.slug !== citySlug; }).slice(0, 8).map(function(city) {
                  return (
                    <Link key={city.slug} to={'/ciudad/centro-rehabilitacion-' + city.slug}>
                      <Button variant="outline" size="sm" className="border-slate-700 hover:border-emerald-500 hover:text-emerald-400">
                        {city.name}
                      </Button>
                    </Link>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
}

export default CitiesDirectoryPage;
